import React from "react";
import JSZip from "jszip";
import { Box, Typography, Button, TextField, Chip, Link, Alert, LinearProgress, Collapse } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { PageContainer, SectionHeader, SelectorBar, SelectField, FormDialog, DataTable, formatDate } from "../Layout/Common";
import { navigate, replaceDeep } from "../Layout/Router";
import api from "../Auth/Api";
import globals from "../Globals";
import { Page } from "../Layout/PageLayout";

// Asset Manager (the backend's AssetRegistry, 2026-09-24): ONE manifest per (branch, platform) whose `seq` grows on
// every change; each entry is a name at its current version on the CDN, owned by "core" (the zip a build uploads)
// or a live-ops owner ("offer:<id>", "event:<id>" - Unity's LiveOpsBundleBuild zip). Reads need `#players` or
// `database`; uploads and retires need `database`.

// Explicit column widths + a fixed table layout: never auto-sized columns (the owner's rule)
export const FIXED = { "& table": { tableLayout: "fixed" } };
export const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };
const UPLOAD_TIMEOUT = 300000;   // a big zip goes to the CDN file by file

export function formatSize(bytes) {
    if (typeof bytes !== "number" || isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

// A compact client version as major.minor.patch; 0 = open ("*")
export function clientLabel(compact) {
    if (!compact) return "*";
    const { major, minor, patch } = globals.decompactVersion(compact);
    return `${major}.${minor}.${patch}`;
}
export const clientRange = (min, max) => (!min && !max ? "any" : `${clientLabel(min)} – ${clientLabel(max)}`);
export const shortSha = (sha) => (sha ? sha.slice(0, 12) : "");
// history rows carry the enums as ints; tolerate names
const branchName = (b) => (typeof b === "number" ? globals.branches[b] : b) ?? "";
const platformName = (p) => (typeof p === "number" ? globals.platformNames[p] : p) ?? "";

// The site account may upload / retire (the `database` role; masters pass everything)
const canEdit = () => {
    if (globals.loginData?.titles?.isMaster) return true;
    const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
    const roles = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
    return roles.includes("database");
};

// The client patches of a branch (versioning list: 5 newest versions per branch), newest first
function clientVersionsOf(versionList, branch) {
    const branchIndex = globals.branches.indexOf(branch);
    const row = (versionList ?? []).find(b => b.branch === branchIndex);
    const seen = new Map();
    for (const v of row?.versions ?? []) {
        const { major, minor } = globals.decompactVersion(v.version);
        for (const c of v.client ?? []) {
            const value = globals.compactVersion(major, minor, c.patch, branchIndex);
            seen.set(value, { value, label: `${major}.${minor}.${c.patch}` });
        }
    }
    return [...seen.values()].sort((a, b) => b.value - a.value);
}

// "1.2.3" (on the given branch) or a compact int -> compact; null when unreadable
function parseClient(text, branch) {
    const t = (text ?? "").trim();
    const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(t);
    if (m) return globals.compactVersion(+m[1], +m[2], +m[3], globals.branches.indexOf(branch));
    return /^\d+$/.test(t) ? parseInt(t, 10) : null;
}

async function sha256Hex(bytes) {
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, "0")).join("");
}

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// The zip's files by path, '\\' normalized to '/' as the backend does
async function openZip(file) {
    const zip = await JSZip.loadAsync(file);
    const files = new Map();
    zip.forEach((path, f) => { if (!f.dir) files.set(path.replace(/\\/g, "/"), f); });
    const mf = files.get("manifest.json");
    if (!mf) throw new Error("manifest.json not found in the zip.");
    let manifest;
    try { manifest = JSON.parse(await mf.async("string")); } catch { throw new Error("manifest.json is not valid JSON."); }
    return { files, manifest };
}

// What an upload did (or would do) to one asset
const STATUS = {
    new: { label: "new", color: "success" },
    bytes: { label: "new bytes", color: "warning" },
    meta: { label: "metadata", color: "info" },
    same: { label: "unchanged", color: "default" },
    missing: { label: "missing file", color: "error" },
    error: { label: "error", color: "error" },
};
const STATUS_ORDER = ["error", "missing", "new", "bytes", "meta", "same"];
const resultStatus = (r) => r.error ? "error" : r.changed ? (r.oldSize ? "bytes" : "new") : r.metaChanged ? "meta" : "same";
const StatusChip = ({ status }) => <Chip size="small" variant="outlined" color={STATUS[status].color} label={STATUS[status].label} />;
const countBy = (rows, key) => rows.reduce((acc, r) => { acc[r[key]] = (acc[r[key]] ?? 0) + 1; return acc; }, {});
const summary = (counts) => ["new", "bytes", "meta", "same", "missing", "error"].filter(k => counts[k]).map(k => `${counts[k]} ${STATUS[k].label}`).join(" · ");

// A core file with the same bytes still moves when its metadata differs from what upload-assets writes
// (owner core, essential, minClient = the chosen version, open max, no deps)
const coreMetaMoves = (e, compact) =>
    e.owner !== "core" || !e.essential || e.minClient !== compact || (e.maxClient ?? 0) !== 0 || (e.deps?.length ?? 0) > 0;

// The history table (get-asset-info rows); `withPlace` adds branch + platform (the Asset Inspector's cross view),
// `urlOf(version)` links a version to its CDN object (versions are never purged)
export function historyColumns({ withPlace = false, urlOf = null } = {}) {
    return [
        ...(withPlace ? [
            { id: "branch", label: "Branch", width: 100, sortable: true, render: r => branchName(r.branch) },
            { id: "platform", label: "Platform", width: 80, sortable: true, render: r => platformName(r.platform) },
        ] : []),
        { id: "version", label: "Ver", width: 60, align: "right", sortable: withPlace,
            render: r => urlOf ? <Link href={urlOf(r.version)} target="_blank" rel="noreferrer">{r.version}</Link> : r.version },
        { id: "size", label: "Size", width: 90, align: "right", sortable: withPlace, render: r => formatSize(r.size) },
        { id: "sha256", label: "sha256", width: 120, sx: ellipsis, render: r => <span title={r.sha256 ?? ""} style={mono}>{shortSha(r.sha256)}</span> },
        { id: "owner", label: "Owner", width: 110, sx: ellipsis, render: r => <span title={r.owner ?? ""}>{r.owner ?? ""}</span> },
        { id: "seq", label: "Seq", width: 60, align: "right", sortable: withPlace },
        { id: "createdDate", label: "Created", width: 160, sortable: withPlace, render: r => formatDate(r.createdDate) },
        { id: "createdBy", label: "By", width: 170, sx: ellipsis, render: r => <span title={r.createdBy ?? ""}>{r.createdBy ?? ""}</span> },
        { id: "retiredDate", label: "Retired", width: 160, render: r => formatDate(r.retiredDate) },
        { id: "note", label: "Note", width: 180, sx: ellipsis, render: r => <span title={r.note ?? ""}>{r.note ?? ""}</span> },
    ];
}

const SORT_KEYS = {
    name: e => e.name,
    owner: e => e.owner ?? "",
    version: e => e.version,
    size: e => e.size,
    seq: e => e.seq,
    updatedDate: e => new Date(e.updatedDate || 0).getTime(),
};

// A scrolling box for the dialogs' tables
const Scroll = ({ children, height = 360 }) => <Box sx={{ maxHeight: height, overflowY: "auto", mt: 1 }}>{children}</Box>;

export default class AssetsPage extends React.Component {
    loadToken = 0;
    state = {
        branch: "Development",
        platform: globals.platformNames[0],
        filter: "",
        manifest: null,
        loading: false,
        error: null,
        versionList: [],
        orderBy: "name",
        order: "asc",
        showTombstones: false,
        history: null,   // { name, rows, error }
        retire: null,    // { name, owner, busy, error }
        core: null,      // { file, client, check, checkError, busy, error, result }
        liveops: null,   // { file, owner, check, checkError, busy, error, result }
        preview: null,   // { client, clientText, seq, busy, error, result }
    };

    componentDidMount() {
        // /<game>/database/assets/<branch>/<platform>; an old link (/<branch>/<clientVersion>/<platform>/<manifest>)
        // still names its branch and platform
        const deep = globals.route?.deep ?? [];
        const branch = globals.branches.includes(deep[0]) ? deep[0] : "Development";
        const platform = deep.slice(1).find(d => globals.platformNames.includes(d)) ?? globals.platformNames[0];
        this.setState({ branch, platform }, () => {
            replaceDeep([branch, platform]);
            // sequenced: two requests in the same millisecond trip the backend's per-device anti-replay
            this.load().finally(() => api.versioning("Loading versions", ["list"], { silent: true })
                .then(list => this.setState({ versionList: Array.isArray(list) ? list : [] }))
                .catch(() => {}));
        });
    }

    load = () => {
        const { branch, platform } = this.state;
        const token = ++this.loadToken;
        this.setState({ loading: true, error: null });
        return api.versioning("Loading assets", ["get-assets", branch, platform], { silent: true })
            .then(m => { if (token === this.loadToken) this.setState({ manifest: m, loading: false }); })
            .catch(err => { if (token === this.loadToken) this.setState({ manifest: null, loading: false, error: err.message }); });
    }

    select = (change) => {
        this.setState(change, () => {
            replaceDeep([this.state.branch, this.state.platform]);
            this.load();
        });
    }

    clientVersions = () => clientVersionsOf(this.state.versionList, this.state.branch);

    sort = (column) => {
        const asc = this.state.orderBy === column && this.state.order === "asc";
        this.setState({ orderBy: column, order: asc ? "desc" : "asc" });
    }

    sortedEntries() {
        const { manifest, filter, orderBy, order } = this.state;
        const f = filter.trim().toLowerCase();
        const rows = (manifest?.entries ?? []).filter(e => !f || e.name.toLowerCase().includes(f) || (e.owner ?? "").toLowerCase().includes(f));
        const key = SORT_KEYS[orderBy] ?? SORT_KEYS.name;
        const dir = order === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = key(a), y = key(b);
            return (x < y ? -1 : x > y ? 1 : 0) * dir || (a.name < b.name ? -1 : 1);
        });
    }

    // ---------- history / retire ----------
    openHistory = (name) => {
        const { branch, platform } = this.state;
        this.setState({ history: { name, rows: null, error: null } });
        api.versioning("Loading history", ["get-asset-info", platform, branch, name, "ALL"], { silent: true })
            .then(rows => this.setState(s => s.history?.name === name
                ? { history: { ...s.history, rows: Array.isArray(rows) ? [...rows].sort((a, b) => b.version - a.version) : [] } } : null))
            .catch(err => this.setState(s => s.history?.name === name ? { history: { ...s.history, rows: [], error: err.message } } : null));
    }

    confirmRetire = () => {
        const { retire, branch, platform } = this.state;
        this.setState({ retire: { ...retire, busy: true, error: null } });
        api.versioning(`Retiring ${retire.name}`, ["retire-asset", branch, platform, retire.name])
            .then(() => { this.setState({ retire: null }); this.load(); })
            .catch(err => this.setState(s => ({ retire: s.retire && { ...s.retire, busy: false, error: err.message } })));
    }

    // ---------- core zip ----------
    openCore = () => this.setState({ core: { file: null, client: this.clientVersions()[0]?.value ?? "", check: null, checkError: null, busy: false, error: null, result: null } });

    pickCoreZip = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        this.setState(s => ({ core: { ...s.core, file, check: null, checkError: null, error: null } }));
        this.checkCoreZip(file)
            .then(check => this.setState(s => s.core?.file === file ? { core: { ...s.core, check } } : null))
            .catch(err => this.setState(s => s.core?.file === file ? { core: { ...s.core, checkError: err.message || String(err) } } : null));
    }

    // What the upload would do to the manifest on screen, read in the browser: the server decides "changed" by its
    // own sha256 of the bytes, computed here the same way; core names missing from the zip get retired
    async checkCoreZip(file) {
        const { files: zipped, manifest: parsed } = await openZip(file);
        const files = parsed?.files ?? {};
        const names = Object.keys(files).sort();
        if (names.length === 0) throw new Error("manifest.json lists no files.");
        const entries = this.state.manifest?.entries ?? [];
        const byName = new Map(entries.map(e => [e.name, e]));
        const rows = [];
        for (const name of names) {
            const entry = byName.get(name) ?? null;
            const f = zipped.get(name);
            if (!f) { rows.push({ name, kind: "missing", size: files[name]?.size ?? 0, oldSize: entry?.size ?? 0, entry }); continue; }
            const bytes = await f.async("uint8array");
            const sha = await sha256Hex(bytes);
            rows.push({ name, kind: !entry ? "new" : entry.sha256 !== sha ? "bytes" : "same", size: bytes.length, oldSize: entry?.size ?? 0, entry });
        }
        const retired = entries.filter(e => e.owner === "core" && !has(files, e.name)).map(e => e.name);
        return { rows, retired, platform: parsed.platform ?? null, total: rows.reduce((t, r) => t + (r.size || 0), 0) };
    }

    uploadCore = () => {
        const { core, platform } = this.state;
        this.setState({ core: { ...core, busy: true, error: null } });
        // a File payload carries no JSON body: every parameter is positional
        api.versioning("Uploading the core zip", ["upload-assets", core.client, platform], { payload: core.file, timeout: UPLOAD_TIMEOUT })
            .then(result => { this.setState(s => ({ core: s.core && { ...s.core, busy: false, result } })); this.load(); })
            .catch(err => this.setState(s => ({ core: s.core && { ...s.core, busy: false, error: err.message } })));
    }

    // ---------- live-ops zip ----------
    openLiveOps = () => this.setState({ liveops: { file: null, owner: "", check: null, checkError: null, busy: false, error: null, result: null } });

    pickLiveOpsZip = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        this.setState(s => ({ liveops: { ...s.liveops, file, check: null, checkError: null, error: null } }));
        this.checkLiveOpsZip(file)
            .then(check => this.setState(s => s.liveops?.file === file ? { liveops: { ...s.liveops, check } } : null))
            .catch(err => this.setState(s => s.liveops?.file === file ? { liveops: { ...s.liveops, checkError: err.message || String(err) } } : null));
    }

    // manifest.json of LiveOpsBundleBuild: { owner, unity, changeset, bundles: [{ name, platform, file, sha256, size, deps }] }
    async checkLiveOpsZip(file) {
        const { files, manifest: parsed } = await openZip(file);
        const bundles = Array.isArray(parsed?.bundles) ? parsed.bundles : [];
        if (bundles.length === 0) throw new Error("manifest.json lists no bundles.");
        const missing = bundles.filter(b => !b.file || !files.has(b.file)).map(b => `${b.name} (${b.platform})`);
        return { owner: parsed.owner ?? "", unity: parsed.unity ?? "", changeset: parsed.changeset ?? 0, bundles, missing };
    }

    uploadLiveOps = () => {
        const { liveops, branch } = this.state;
        const owner = liveops.owner.trim();
        this.setState({ liveops: { ...liveops, busy: true, error: null } });
        api.versioning("Uploading the live-ops zip", ["upload-liveops", branch, ...(owner ? [owner] : [])], { payload: liveops.file, timeout: UPLOAD_TIMEOUT })
            .then(result => { this.setState(s => ({ liveops: s.liveops && { ...s.liveops, busy: false, result } })); this.load(); })
            .catch(err => this.setState(s => ({ liveops: s.liveops && { ...s.liveops, busy: false, error: err.message } })));
    }

    // ---------- preview ----------
    openPreview = () => this.setState({ preview: { client: this.clientVersions()[0]?.value ?? "", clientText: "", seq: "0", busy: false, error: null, result: null } });

    previewClient() {
        const { preview, branch } = this.state;
        return preview.clientText.trim() ? parseClient(preview.clientText, branch) : (preview.client === "" ? null : preview.client);
    }

    runPreview = () => {
        const { preview, branch, platform } = this.state;
        const compact = this.previewClient();
        const seq = Math.max(0, parseInt(preview.seq, 10) || 0);
        if (compact == null) return;
        this.setState({ preview: { ...preview, busy: true, error: null } });
        api.versioning("Previewing", ["asset-preview", branch, platform, compact, seq], { silent: true })
            .then(result => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, result: { ...result, askedSeq: seq, compact } } })))
            .catch(err => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, error: err.message, result: null } })));
    }

    // ---------- render ----------
    renderEntries() {
        const { orderBy, order, manifest, loading, branch, platform } = this.state;
        const edit = canEdit();
        const rows = this.sortedEntries();
        const columns = [
            { id: "name", label: "Name", width: 260, sortable: true, sx: ellipsis, render: e => <span title={e.name}>{e.name}</span> },
            { id: "owner", label: "Owner", width: 120, sortable: true, sx: ellipsis, render: e => <span title={e.owner ?? ""}>{e.owner ?? ""}</span> },
            { id: "version", label: "Ver", width: 70, align: "right", sortable: true,
                render: e => e.url ? <Link href={e.url} target="_blank" rel="noreferrer" title={e.url}>{e.version}</Link> : e.version },
            { id: "size", label: "Size", width: 90, align: "right", sortable: true, render: e => formatSize(e.size) },
            { id: "essential", label: "Essential", width: 80, align: "center", render: e => e.essential ? <CheckIcon fontSize="small" color="success" /> : "" },
            { id: "client", label: "Client range", width: 140, sx: ellipsis,
                render: e => <span title={`minClient ${e.minClient ?? 0} · maxClient ${e.maxClient ?? 0}`}>{clientRange(e.minClient, e.maxClient)}</span> },
            { id: "deps", label: "Deps", width: 170, sx: ellipsis, render: e => { const d = (e.deps ?? []).join(", "); return <span title={d}>{d}</span>; } },
            { id: "seq", label: "Seq", width: 60, align: "right", sortable: true },
            { id: "updatedDate", label: "Updated", width: 160, sortable: true, render: e => formatDate(e.updatedDate) },
            { id: "actions", label: "", width: edit ? 160 : 90, render: e => (
                <Box sx={{ display: "flex", gap: 0.5, whiteSpace: "nowrap" }}>
                    <Button size="small" onClick={() => this.openHistory(e.name)}>History</Button>
                    {edit && <Button size="small" color="error" onClick={() => this.setState({ retire: { name: e.name, owner: e.owner, busy: false, error: null } })}>Retire</Button>}
                </Box>
            )},
        ];
        if (!loading && manifest && rows.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {(manifest.entries ?? []).length ? "No asset matches the filter." : `No assets on ${branch} ${platform}.`}
            </Typography>;
        }
        return <DataTable columns={columns} rows={rows} getRowKey={e => e.name} orderBy={orderBy} order={order} onSort={this.sort} sx={FIXED} />;
    }

    renderTombstones() {
        const { manifest, showTombstones } = this.state;
        const removed = [...(manifest?.removed ?? [])].sort((a, b) => b.seq - a.seq);
        if (removed.length === 0) return null;
        return (
            <Box sx={{ mt: 2 }}>
                <Button size="small" onClick={() => this.setState({ showTombstones: !showTombstones })}
                        startIcon={showTombstones ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                    Tombstones ({removed.length})
                </Button>
                <Collapse in={showTombstones}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Names retired from this manifest: a client holding an older seq is told to delete them.
                    </Typography>
                    <DataTable sx={{ ...FIXED, maxWidth: 640, mt: 1 }} getRowKey={t => t.name} rows={removed} columns={[
                        { id: "name", label: "Name", width: 380, sx: ellipsis, render: t => <span title={t.name}>{t.name}</span> },
                        { id: "seq", label: "Seq", width: 80, align: "right" },
                        { id: "date", label: "Retired", width: 180, render: t => formatDate(t.date) },
                    ]} />
                </Collapse>
            </Box>
        );
    }

    renderHistoryDialog() {
        const { history, branch, platform, manifest } = this.state;
        if (!history) return null;
        // the CDN keeps every version: Assets/{title}/{branch}/{platform}/{name}/{version}
        const url = manifest?.entries?.find(e => e.name === history.name)?.url;
        const urlOf = url ? (v) => url.replace(/\/\d+$/, `/${v}`) : null;
        return (
            <FormDialog open title={`History · ${history.name}`} onClose={() => this.setState({ history: null })} hideConfirm cancelText="Close"
                        maxWidth={false} PaperProps={{ sx: { width: 1200, maxWidth: "95vw" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">{branch} · {platform} · every upload that changed the bytes</Typography>
                    <Button size="small" sx={{ ml: "auto" }} onClick={() => navigate({ title: globals.selectedTitle, page: Page.Database.id,
                        sub: Page.Database.subs.AssetInspector.id, deep: ["ALL", "ALL", history.name] })}>
                        All branches &amp; platforms
                    </Button>
                </Box>
                {history.rows == null && <LinearProgress sx={{ mt: 2 }} />}
                {history.error && <Alert severity="error" sx={{ mt: 2 }}>{history.error}</Alert>}
                {history.rows && history.rows.length === 0 && !history.error && <Typography sx={{ mt: 2 }}>No history rows.</Typography>}
                {history.rows && history.rows.length > 0 && (
                    <Scroll height={480}>
                        <DataTable sx={FIXED} columns={historyColumns({ urlOf })} rows={history.rows} getRowKey={r => `${r.version}`} />
                    </Scroll>
                )}
            </FormDialog>
        );
    }

    renderRetireDialog() {
        const { retire, branch, platform } = this.state;
        if (!retire) return null;
        return (
            <FormDialog open title={`Retire ${retire.name}?`} onClose={() => this.setState({ retire: null })}
                        confirmText="Retire" onConfirm={this.confirmRetire} confirmDisabled={retire.busy} cancelDisabled={retire.busy}>
                <Typography variant="body2">
                    Drops it from the {branch} {platform} manifest (owner {retire.owner || "?"}): clients delete it on their next sync.
                    The CDN objects and the history stay; uploading the name again resumes its version count.
                </Typography>
                {retire.owner === "core" && <Alert severity="warning" sx={{ mt: 2 }}>A core asset: the next core zip that carries it registers it again.</Alert>}
                {retire.error && <Alert severity="error" sx={{ mt: 2 }}>{retire.error}</Alert>}
            </FormDialog>
        );
    }

    renderCoreDialog() {
        const { core, branch, platform } = this.state;
        if (!core) return null;
        const versions = this.clientVersions();
        const { check, result } = core;
        const checkRows = (check?.rows ?? []).map(r => ({ ...r, status: r.kind === "same" && r.entry && coreMetaMoves(r.entry, core.client) ? "meta" : r.kind }));
        const missing = checkRows.some(r => r.status === "missing");
        const resultRows = result ? Object.entries(result.updated ?? {}).map(([name, u]) => ({ name, ...u, size: u.newSize, status: resultStatus(u) })) : [];
        const fileColumns = [
            { id: "name", label: "Name", width: 320, sx: ellipsis, render: r => <span title={r.name}>{r.name}</span> },
            { id: "status", label: "", width: 130, render: r => <StatusChip status={r.status} /> },
            ...(result ? [{ id: "version", label: "Ver", width: 60, align: "right" }] : []),
            { id: "size", label: "Size", width: 90, align: "right", render: r => formatSize(r.size) },
            { id: "oldSize", label: "Was", width: 90, align: "right", render: r => r.oldSize ? formatSize(r.oldSize) : "" },
        ];
        const listed = (rows) => rows.filter(r => r.status !== "same").sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || a.name.localeCompare(b.name));
        const retired = result ? (result.removed ?? []) : (check?.retired ?? []);
        return (
            <FormDialog open title={result ? `Core zip uploaded · seq ${result.seq}` : `Upload core zip · ${branch} ${platform}`}
                        onClose={() => this.setState({ core: null })} maxWidth={false} PaperProps={{ sx: { width: 820, maxWidth: "95vw" } }}
                        confirmText="Upload" onConfirm={this.uploadCore} hideConfirm={!!result} cancelText={result ? "Close" : "Cancel"}
                        confirmDisabled={!core.file || core.client === "" || core.busy || missing} cancelDisabled={core.busy}>
                {!result && <>
                    <Typography variant="body2" color="text.secondary">
                        The core set as a snapshot: every file is registered essential with minClient = the chosen version (same
                        bytes keep their version), core names missing from the zip are retired. Live-ops entries are not touched.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
                        <SelectField label="Client version" size="small" minWidth={160} value={core.client} options={versions}
                                     disabled={core.busy} onChange={client => this.setState(s => ({ core: { ...s.core, client } }))} />
                        <Button variant="outlined" component="label" disabled={core.busy}>
                            Choose zip
                            <input type="file" accept=".zip" hidden onChange={this.pickCoreZip} />
                        </Button>
                        {core.file && <Typography variant="body2" sx={ellipsis}>{core.file.name} · {formatSize(core.file.size)}</Typography>}
                    </Box>
                    {versions.length === 0 && <Alert severity="warning" sx={{ mt: 2 }}>No client patch on {branch}: register one on the Versioning page first.</Alert>}
                    {core.file && !check && !core.checkError && <LinearProgress sx={{ mt: 2 }} />}
                    {core.checkError && <Alert severity="warning" sx={{ mt: 2 }}>Could not read the zip here: {core.checkError}</Alert>}
                    {check?.platform && check.platform !== platform &&
                        <Alert severity="warning" sx={{ mt: 2 }}>The zip says platform {check.platform}; it would be uploaded to {platform}.</Alert>}
                    {missing && <Alert severity="error" sx={{ mt: 2 }}>manifest.json lists files the zip does not carry: the server refuses it.</Alert>}
                    {check && <>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            {check.rows.length} files ({formatSize(check.total)}) · {summary(countBy(checkRows, "status")) || "no changes"}
                            {check.retired.length ? ` · ${check.retired.length} retired` : ""}
                        </Typography>
                        {listed(checkRows).length > 0 && <Scroll><DataTable sx={FIXED} columns={fileColumns} rows={listed(checkRows)} getRowKey={r => r.name} /></Scroll>}
                    </>}
                </>}
                {result && <>
                    <Typography variant="body2">{resultRows.length} files · {summary(countBy(resultRows, "status")) || "no changes"}{retired.length ? ` · ${retired.length} retired` : ""}</Typography>
                    {listed(resultRows).length > 0 && <Scroll><DataTable sx={FIXED} columns={fileColumns} rows={listed(resultRows)} getRowKey={r => r.name} /></Scroll>}
                </>}
                {retired.length > 0 && <Typography variant="body2" color="error.main" sx={{ mt: 1, wordBreak: "break-word" }}>
                    {result ? "Retired" : "Will be retired"}: {retired.join(", ")}
                </Typography>}
                {core.busy && <LinearProgress sx={{ mt: 2 }} />}
                {core.error && <Alert severity="error" sx={{ mt: 2 }}>{core.error}</Alert>}
            </FormDialog>
        );
    }

    renderLiveOpsDialog() {
        const { liveops, branch } = this.state;
        if (!liveops) return null;
        const { check, result } = liveops;
        const badOwner = /\s/.test(liveops.owner.trim());
        const noOwner = check && !check.owner && !liveops.owner.trim();
        const byPlatform = (a, b) => (a.platform ?? "").localeCompare(b.platform ?? "") || (a.name ?? "").localeCompare(b.name ?? "");
        const bundleRows = result ? [...(result.bundles ?? [])].map(b => ({ ...b, status: resultStatus(b) })).sort(byPlatform) : [];
        return (
            <FormDialog open title={result ? `Live-ops zip uploaded · ${result.owner}` : `Upload live-ops zip · ${branch}`}
                        onClose={() => this.setState({ liveops: null })} maxWidth={false} PaperProps={{ sx: { width: 980, maxWidth: "95vw" } }}
                        confirmText="Upload" onConfirm={this.uploadLiveOps} hideConfirm={!!result} cancelText={result ? "Close" : "Cancel"}
                        confirmDisabled={!liveops.file || liveops.busy || badOwner || noOwner || (check?.missing.length ?? 0) > 0} cancelDisabled={liveops.busy}>
                {!result && <>
                    <Typography variant="body2" color="text.secondary">
                        A LiveOpsBundleBuild zip: every platform's bundles in one file, registered under the owner on {branch}.
                        Bundles are never essential by themselves (the game's sync marks them while their slot is live).
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
                        <Button variant="outlined" component="label" disabled={liveops.busy}>
                            Choose zip
                            <input type="file" accept=".zip" hidden onChange={this.pickLiveOpsZip} />
                        </Button>
                        <TextField size="small" label="Owner (optional)" value={liveops.owner} disabled={liveops.busy} sx={{ width: 260 }}
                                   placeholder={check?.owner || "offer:<id> / event:<id>"} error={badOwner}
                                   helperText={badOwner ? "No spaces" : undefined}
                                   onChange={e => { const owner = e.target.value; this.setState(s => ({ liveops: { ...s.liveops, owner } })); }} />
                        {liveops.file && <Typography variant="body2" sx={ellipsis}>{liveops.file.name} · {formatSize(liveops.file.size)}</Typography>}
                    </Box>
                    {liveops.file && !check && !liveops.checkError && <LinearProgress sx={{ mt: 2 }} />}
                    {liveops.checkError && <Alert severity="warning" sx={{ mt: 2 }}>Could not read the zip here: {liveops.checkError}</Alert>}
                    {noOwner && <Alert severity="error" sx={{ mt: 2 }}>The zip names no owner: type one.</Alert>}
                    {check?.missing.length > 0 && <Alert severity="error" sx={{ mt: 2 }}>Files missing from the zip: {check.missing.join(", ")}</Alert>}
                    {check && <>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            owner {liveops.owner.trim() || check.owner || "?"}{check.unity ? ` · Unity ${check.unity}` : ""}{check.changeset ? ` · cs:${check.changeset}` : ""}
                            {" · "}{check.bundles.length} bundles
                        </Typography>
                        <Scroll>
                            <DataTable sx={FIXED} rows={[...check.bundles].sort(byPlatform)} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                                { id: "platform", label: "Platform", width: 90 },
                                { id: "name", label: "Name", width: 300, sx: ellipsis, render: b => <span title={b.name}>{b.name}</span> },
                                { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                                { id: "deps", label: "Deps", width: 260, sx: ellipsis, render: b => { const d = (b.deps ?? []).join(", "); return <span title={d}>{d}</span>; } },
                            ]} />
                        </Scroll>
                    </>}
                </>}
                {result && <>
                    <Typography variant="body2">
                        {summary(countBy(bundleRows, "status")) || "no bundles"} · seq {Object.entries(result.seq ?? {}).map(([p, n]) => `${p} ${n}`).join(", ") || "-"}
                    </Typography>
                    <Scroll>
                        <DataTable sx={FIXED} rows={bundleRows} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                            { id: "platform", label: "Platform", width: 90 },
                            { id: "name", label: "Name", width: 240, sx: ellipsis, render: b => <span title={b.name}>{b.name}</span> },
                            { id: "status", label: "", width: 120, render: b => <StatusChip status={b.status} /> },
                            { id: "version", label: "Ver", width: 60, align: "right" },
                            { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                            { id: "oldSize", label: "Was", width: 90, align: "right", render: b => b.oldSize ? formatSize(b.oldSize) : "" },
                            { id: "error", label: "Error", width: 220, sx: ellipsis, render: b => <span title={b.error ?? ""}>{b.error ?? ""}</span> },
                        ]} />
                    </Scroll>
                </>}
                {liveops.busy && <LinearProgress sx={{ mt: 2 }} />}
                {liveops.error && <Alert severity="error" sx={{ mt: 2 }}>{liveops.error}</Alert>}
            </FormDialog>
        );
    }

    renderPreviewDialog() {
        const { preview, branch, platform } = this.state;
        if (!preview) return null;
        const versions = this.clientVersions();
        const compact = this.previewClient();
        const r = preview.result;
        const changed = r?.changed ?? [];
        const setField = (field) => (value) => this.setState(s => ({ preview: { ...s.preview, [field]: value } }));
        return (
            <FormDialog open title={`Preview for a client · ${branch} ${platform}`} onClose={() => this.setState({ preview: null })}
                        maxWidth={false} PaperProps={{ sx: { width: 960, maxWidth: "95vw" } }} cancelText="Close"
                        confirmText="Preview" onConfirm={this.runPreview} confirmDisabled={compact == null || preview.busy}>
                <Typography variant="body2" color="text.secondary">
                    What the session-start sync tells a client at this version that holds this manifest seq (0 = nothing yet).
                    Live-ops bundles show as essential only when the game marks their slot live, which this preview does not.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 2 }}>
                    {versions.length > 0 && <SelectField label="Client version" size="small" minWidth={160} value={preview.client} options={versions}
                                                         onChange={setField("client")} disabled={!!preview.clientText.trim()} />}
                    <TextField size="small" label={versions.length ? "or type (M.m.p / compact)" : "Client (M.m.p / compact)"} sx={{ width: 220 }}
                               value={preview.clientText} onChange={e => setField("clientText")(e.target.value)}
                               error={!!preview.clientText.trim() && compact == null} />
                    <TextField size="small" label="Seq held" type="number" sx={{ width: 120 }} value={preview.seq}
                               onChange={e => setField("seq")(e.target.value)}
                               onKeyDown={e => { if (e.key === "Enter" && compact != null) this.runPreview(); }} />
                    {compact != null && <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>{clientLabel(compact)} ({compact})</Typography>}
                </Box>
                {preview.busy && <LinearProgress sx={{ mt: 2 }} />}
                {preview.error && <Alert severity="error" sx={{ mt: 2 }}>{preview.error}</Alert>}
                {r && <>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        seq {r.seq} · {r.full ? "full list (the client replaces what it holds)" : `delta since seq ${r.askedSeq}`}
                        {" · "}{changed.length} to download ({formatSize(changed.reduce((t, c) => t + (c.size || 0), 0))}, {changed.filter(c => c.essential).length} essential)
                        {" · "}{(r.removed ?? []).length} to delete
                    </Typography>
                    {changed.length > 0 && <Scroll>
                        <DataTable sx={FIXED} rows={changed} getRowKey={c => c.name} columns={[
                            { id: "name", label: "Name", width: 280, sx: ellipsis, render: c => <span title={c.name}>{c.name}</span> },
                            { id: "version", label: "Ver", width: 60, align: "right" },
                            { id: "size", label: "Size", width: 90, align: "right", render: c => formatSize(c.size) },
                            { id: "essential", label: "Essential", width: 90, align: "center", render: c => c.essential ? <CheckIcon fontSize="small" color="success" /> : "" },
                            { id: "url", label: "URL", width: 360, sx: ellipsis, render: c => <Link href={c.url} target="_blank" rel="noreferrer" title={c.url} sx={mono}>{c.url}</Link> },
                        ]} />
                    </Scroll>}
                    {(r.removed ?? []).length > 0 && <Typography variant="body2" color="error.main" sx={{ mt: 1, wordBreak: "break-word" }}>
                        Delete: {r.removed.join(", ")}
                    </Typography>}
                </>}
            </FormDialog>
        );
    }

    render() {
        const { branch, platform, filter, manifest, loading, error } = this.state;
        const edit = canEdit();
        const entries = manifest?.entries ?? [];
        const total = entries.reduce((t, e) => t + (e.size || 0), 0);
        return (
            <PageContainer maxWidth={1360}>
                <SectionHeader title="Asset Manager">
                    <Button variant="outlined" onClick={this.openPreview}>Preview for a client</Button>
                    {edit && <Button variant="contained" onClick={this.openCore}>Upload core zip</Button>}
                    {edit && <Button variant="contained" onClick={this.openLiveOps}>Upload live-ops zip</Button>}
                </SectionHeader>
                <SelectorBar sx={{ alignItems: "center" }}>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches}
                                 onChange={b => this.select({ branch: b, manifest: null })} />
                    <SelectField label="Platform" size="small" minWidth={160} value={platform} options={globals.platformNames}
                                 onChange={p => this.select({ platform: p, manifest: null })} />
                    <TextField size="small" label="Filter (name / owner)" value={filter} sx={{ width: 220 }}
                               onChange={e => this.setState({ filter: e.target.value })} />
                    <Button onClick={this.load} disabled={loading}>Reload</Button>
                    {manifest && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto", textAlign: "right" }}>
                            {manifest.seq
                                ? <>seq {manifest.seq} · updated {formatDate(manifest.updatedDate)} by {manifest.updatedBy || "?"}</>
                                : "no manifest yet"}
                            <br />{entries.length} entries · {formatSize(total)}
                        </Typography>
                    )}
                </SelectorBar>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {this.renderEntries()}
                {this.renderTombstones()}
                {this.renderHistoryDialog()}
                {this.renderRetireDialog()}
                {this.renderCoreDialog()}
                {this.renderLiveOpsDialog()}
                {this.renderPreviewDialog()}
            </PageContainer>
        );
    }
}
